import { MAX_INSPIRATION_DATA_LENGTH, type CustomInspirationPhoto } from '../../shared/customInspirationPhoto'

/** Re-encode on the device to bound upload size and omit the original photo metadata. */
export async function prepareInspirationPhoto(file: File): Promise<CustomInspirationPhoto> {
  if (!file.size || file.size > 25 * 1024 * 1024) throw Error('Choose a photo smaller than 25 MB.')
  if (!/^image\/(jpeg|png|webp|heic|heif|avif)$/i.test(file.type) && !(file.type === '' && /\.(jpe?g|png|webp|heic|heif|avif)$/i.test(file.name))) throw Error('Choose a photo from your library, such as a JPEG or PNG.')
  const url = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const photo = new Image()
      const timer = window.setTimeout(() => { photo.src = ''; reject(Error('This photo took too long to open. Try a smaller photo.')) }, 20000)
      photo.onload = () => { clearTimeout(timer); resolve(photo) }
      photo.onerror = () => { clearTimeout(timer); reject(Error('Your browser could not open this photo. Try a JPEG, PNG, or a screenshot.')) }
      photo.src = url
    })
    if (!image.naturalWidth || !image.naturalHeight) throw Error('This photo could not be opened. Try another photo.')
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext('2d')
    if (!context) throw Error('Your browser could not prepare the photo. Please try again.')
    for (let attempt = 0; attempt < 5; attempt++) {
      context.fillStyle = '#ffffff'; context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)
      const dataUrl = canvas.toDataURL('image/jpeg', Math.max(0.55, 0.85 - attempt * 0.08))
      if (dataUrl.startsWith('data:image/jpeg;base64,') && dataUrl.length <= MAX_INSPIRATION_DATA_LENGTH) return { dataUrl, name: file.name.slice(0, 120), width: canvas.width, height: canvas.height }
      canvas.width = Math.max(1, Math.round(canvas.width * 0.8)); canvas.height = Math.max(1, Math.round(canvas.height * 0.8))
    }
    throw Error('This photo is too detailed to attach. Try a smaller photo or a screenshot.')
  } finally { URL.revokeObjectURL(url) }
}
